import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronDown, X, Check, Info, Scale, Ruler, Layers, Maximize, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { categories, Product } from '../data/products';
// Flatten all products
const allProducts = categories.flatMap(cat => cat.products.map(p => ({
  ...p,
  categoryName: cat.name
})));
export function CompareMaterials() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Support up to 3 selections on desktop, 2 on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const maxSelections = isMobile ? 2 : 3;
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const handleSelect = (index: number, slug: string) => {
    const newSelections = [...selectedSlugs];
    newSelections[index] = slug;
    setSelectedSlugs(newSelections);
  };
  const handleClear = (index: number) => {
    const newSelections = [...selectedSlugs];
    newSelections.splice(index, 1);
    setSelectedSlugs(newSelections);
  };
  const clearAll = () => {
    setSelectedSlugs([]);
  };
  const getProduct = (slug: string) => allProducts.find(p => p.slug === slug);
  return <div className="pt-24 pb-16 bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-2305">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2306">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-2307">
          <div data-id="element-2308">
            <Link to="/#materials" className="inline-flex items-center text-brand-muted hover:text-brand-orange transition-colors mb-4 font-bold text-sm" data-id="element-2309">
              <ArrowLeft className="w-4 h-4 mr-1" data-id="element-2310" />
              Back to Materials Hub
            </Link>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-2311">
              Compare Materials
            </h1>
            <p className="text-brand-muted dark:text-stone-300 mt-2 font-medium text-lg" data-id="element-2312">
              Select up to {maxSelections} products to compare specs and
              details.
            </p>
          </div>
          {selectedSlugs.length > 0 && <button onClick={clearAll} className="text-sm font-bold text-brand-muted hover:text-brand-orange transition-colors self-start md:self-auto" data-id="element-2313">
              Clear All
            </button>}
        </div>

        {/* Selectors */}
        <div className={`grid gap-4 mb-8 ${isMobile ? 'grid-cols-2' : 'grid-cols-3'}`} data-id="element-2314">
          {Array.from({
          length: maxSelections
        }).map((_, i) => {
          const selectedSlug = selectedSlugs[i];
          const selectedProduct = selectedSlug ? getProduct(selectedSlug) : null;
          return <div key={i} className="bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 p-4 shadow-sm relative" data-id="element-2315">
                {selectedProduct ? <div className="flex items-center justify-between" data-id="element-2316">
                    <div className="flex items-center gap-3 overflow-hidden" data-id="element-2317">
                      <img src={selectedProduct.image} alt={selectedProduct.name} className="w-12 h-12 rounded-lg object-cover shrink-0" data-id="element-2318" />
                      <div className="truncate" data-id="element-2319">
                        <p className="text-xs font-bold text-brand-muted uppercase tracking-wider truncate" data-id="element-2320">
                          {selectedProduct.categoryName}
                        </p>
                        <p className="text-sm font-extrabold text-brand-charcoal dark:text-white truncate" data-id="element-2321">
                          {selectedProduct.name}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => handleClear(i)} className="p-1.5 text-brand-muted hover:text-brand-orange transition-colors shrink-0" data-id="element-2322">
                      <X className="w-4 h-4" data-id="element-2323" />
                    </button>
                  </div> : <div className="relative" data-id="element-2324">
                    <select className="w-full appearance-none bg-stone-50 dark:bg-[#2a2a2a] border border-stone-200 dark:border-white/10 rounded-lg py-3 px-4 text-sm font-bold text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange" value="" onChange={e => handleSelect(i, e.target.value)} data-id="element-2325">
                      <option value="" disabled className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white" data-id="element-2326">
                        Select a product...
                      </option>
                      {categories.map(cat => <optgroup key={cat.name} label={cat.name} className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white font-bold" data-id="element-2327">
                          {cat.products.map(p => <option key={p.slug} value={p.slug} disabled={selectedSlugs.includes(p.slug)} className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white disabled:text-stone-400 dark:disabled:text-stone-500" data-id="element-2328">
                              {p.name}
                            </option>)}
                        </optgroup>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted pointer-events-none" data-id="element-2329" />
                  </div>}
              </div>;
        })}
        </div>

        {/* Comparison Table / Cards */}
        {selectedSlugs.length === 0 ? <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 p-12 text-center shadow-sm" data-id="element-2330">
            <div className="w-16 h-16 bg-stone-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2331">
              <Scale className="w-8 h-8 text-brand-muted" data-id="element-2332" />
            </div>
            <h3 className="text-xl font-extrabold text-brand-charcoal dark:text-white mb-2" data-id="element-2333">
              No products selected
            </h3>
            <p className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-2334">
              Use the dropdowns above to select materials to compare.
            </p>
          </div> : <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm overflow-hidden" data-id="element-2335">
            <div className="overflow-x-auto" data-id="element-2336">
              <table className="w-full text-left border-collapse" data-id="element-2337">
                <thead data-id="element-2338">
                  <tr className="border-b border-stone-200 dark:border-white/10 bg-stone-50 dark:bg-white/5" data-id="element-2339">
                    <th className="p-4 font-bold text-brand-muted w-1/4" data-id="element-2340">
                      Feature
                    </th>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <th key={i} className={`p-4 font-extrabold text-brand-charcoal dark:text-white ${isMobile ? 'w-[37.5%]' : 'w-1/4'} border-l border-stone-200 dark:border-white/10`} data-id="element-2341">
                          {product ? product.name : '-'}
                        </th>;
                })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-2342">
                  {/* Price */}
                  <tr data-id="element-2343">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300 flex items-center gap-2" data-id="element-2344">
                      Price / Yard
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 font-extrabold text-brand-charcoal dark:text-white text-lg" data-id="element-2345">
                          {product ? `$${product.pricePerYard}` : '-'}
                        </td>;
                })}
                  </tr>

                  {/* Size */}
                  <tr data-id="element-2346">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2347">
                      <div className="flex items-center gap-2" data-id="element-2348">
                        <Ruler className="w-4 h-4 text-brand-muted" data-id="element-2349" />
                        Size
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2350">
                          {product?.specs?.size || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Weight */}
                  <tr data-id="element-2351">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2352">
                      <div className="flex items-center gap-2" data-id="element-2353">
                        <Scale className="w-4 h-4 text-brand-muted" data-id="element-2354" />
                        Weight / Yard
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2355">
                          {product?.specs?.weightPerYard || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Compaction */}
                  <tr data-id="element-2356">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2357">
                      <div className="flex items-center gap-2" data-id="element-2358">
                        <Layers className="w-4 h-4 text-brand-muted" data-id="element-2359" />
                        Compaction
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2360">
                          {product?.specs?.compaction || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Coverage */}
                  <tr data-id="element-2361">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2362">
                      <div className="flex items-center gap-2" data-id="element-2363">
                        <Maximize className="w-4 h-4 text-brand-muted" data-id="element-2364" />
                        Coverage
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2365">
                          {product?.specs?.coverage || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Best For */}
                  <tr data-id="element-2366">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2367">
                      <div className="flex items-center gap-2" data-id="element-2368">
                        <Check className="w-4 h-4 text-brand-muted" data-id="element-2369" />
                        Best For
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10" data-id="element-2370">
                          {product?.bestFor ? <div className="flex flex-wrap gap-1.5" data-id="element-2371">
                              {product.bestFor.map(tag => <span key={tag} className="inline-block px-2 py-1 bg-stone-100 dark:bg-white/10 text-brand-charcoal dark:text-stone-300 text-xs font-bold rounded-md" data-id="element-2372">
                                  {tag}
                                </span>)}
                            </div> : '-'}
                        </td>;
                })}
                  </tr>

                  {/* Pro Tip */}
                  <tr data-id="element-2373">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2374">
                      <div className="flex items-center gap-2" data-id="element-2375">
                        <Lightbulb className="w-4 h-4 text-brand-orange" data-id="element-2376" />
                        Pro Tip
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-400 italic" data-id="element-2377">
                          {product?.proTip ? `"${product.proTip}"` : '-'}
                        </td>;
                })}
                  </tr>

                  {/* View Details Link */}
                  <tr data-id="element-2378">
                    <td className="p-4" data-id="element-2379"></td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-center" data-id="element-2380">
                          {product && <Link to={`/shop/${product.slug}`} className="inline-block w-full py-2 bg-stone-100 dark:bg-white/10 hover:bg-stone-200 dark:hover:bg-white/20 text-brand-charcoal dark:text-white text-sm font-bold rounded-lg transition-colors" data-id="element-2381">
                              View Details
                            </Link>}
                        </td>;
                })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}